/**
 * Pivot — transform a start word into a target word using three operations:
 *   swap  — change any letter (cost 10, counts against par)
 *   slide — rearrange into an anagram (cost 8, counts against par)
 *   bend  — change a letter to a keyboard neighbour (free, never counts)
 *
 * Ported from iOS `PivotPuzzle.swift`, `PivotGameSession.swift` and
 * `QWERTYAdjacency.swift`.
 */

export type PivotOperation = 'swap' | 'slide' | 'bend';

/**
 * Bend is free by design — it also doesn't count against par or the quest's
 * free-move budget. Noticing that an adjacent letter solves the next step is
 * the skill the design pays for.
 */
export const OPERATION_COST: Record<PivotOperation, number> = {
  swap: 10,
  slide: 8,
  bend: 0,
};

export interface PivotMove {
  operation: PivotOperation;
  fromWord: string;
  toWord: string;
  /** Position of the changed letter; null for slide. */
  position: number | null;
}

export interface PivotPuzzle {
  startWord: string;
  targetWord: string;
  par: number;
  parSolution: PivotMove[];
  difficulty: string;
  theme: string | null;
}

/** Raw Firestore shape. */
export interface PivotLevel {
  startWord?: string;
  targetWord?: string;
  par?: unknown;
  difficulty?: string;
  theme?: string | null;
  parSolution?: Array<{
    operation?: string;
    fromWord?: string;
    toWord?: string;
    position?: number | null;
  }>;
}

/** Billable moves allowed past par before each extra one costs a life. */
export const MAX_FREE_MOVES_PAST_PAR = 2;

const OPERATIONS: PivotOperation[] = ['swap', 'slide', 'bend'];

function intValue(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return Math.trunc(raw);
  if (typeof raw === 'string') {
    const n = parseInt(raw, 10);
    if (!Number.isNaN(n)) return n;
  }
  return null;
}

export function normalizePuzzle(raw: PivotLevel | null): PivotPuzzle | null {
  if (!raw) return null;
  const startWord = (raw.startWord ?? '').toUpperCase();
  const targetWord = (raw.targetWord ?? '').toUpperCase();
  if (!startWord || !targetWord || startWord.length !== targetWord.length) return null;

  const parSolution: PivotMove[] = [];
  for (const m of raw.parSolution ?? []) {
    const op = String(m?.operation ?? '').toLowerCase();
    if (!(OPERATIONS as string[]).includes(op)) continue;
    parSolution.push({
      operation: op as PivotOperation,
      fromWord: String(m?.fromWord ?? '').toUpperCase(),
      toWord: String(m?.toWord ?? '').toUpperCase(),
      position: m?.position ?? null,
    });
  }

  return {
    startWord,
    targetWord,
    par: intValue(raw.par) ?? parSolution.length,
    parSolution,
    difficulty: raw.difficulty ?? 'standard',
    theme: raw.theme || null,
  };
}

/**
 * Physical-keyboard neighbour map (8-direction, including diagonals). A Bend
 * may only change a letter to one of these.
 */
export const QWERTY_ADJACENCY: Record<string, string[]> = {
  Q: ['W', 'A', 'S'],
  W: ['Q', 'E', 'A', 'S', 'D'],
  E: ['W', 'R', 'S', 'D', 'F'],
  R: ['E', 'T', 'D', 'F', 'G'],
  T: ['R', 'Y', 'F', 'G', 'H'],
  Y: ['T', 'U', 'G', 'H', 'J'],
  U: ['Y', 'I', 'H', 'J', 'K'],
  I: ['U', 'O', 'J', 'K', 'L'],
  O: ['I', 'P', 'K', 'L'],
  P: ['O', 'L'],
  A: ['Q', 'W', 'S', 'Z'],
  S: ['A', 'W', 'E', 'D', 'Z', 'X', 'Q'],
  D: ['S', 'E', 'R', 'F', 'X', 'C', 'W'],
  F: ['D', 'R', 'T', 'G', 'C', 'V', 'E'],
  G: ['F', 'T', 'Y', 'H', 'V', 'B', 'R'],
  H: ['G', 'Y', 'U', 'J', 'B', 'N', 'T'],
  J: ['H', 'U', 'I', 'K', 'N', 'M', 'Y'],
  K: ['J', 'I', 'O', 'L', 'M', 'U'],
  L: ['K', 'O', 'P', 'I'],
  Z: ['A', 'S', 'X'],
  X: ['Z', 'S', 'D', 'C'],
  C: ['X', 'D', 'F', 'V'],
  V: ['C', 'F', 'G', 'B'],
  B: ['V', 'G', 'H', 'N'],
  N: ['B', 'H', 'J', 'M'],
  M: ['N', 'J', 'K'],
};

export function areAdjacent(a: string, b: string): boolean {
  const upA = a.toUpperCase();
  const upB = b.toUpperCase();
  if (upA === upB) return false;
  return (QWERTY_ADJACENCY[upA] ?? []).includes(upB);
}

/** Neighbours of `letter`, sorted for stable UI ordering. */
export function adjacentLetters(letter: string): string[] {
  return [...(QWERTY_ADJACENCY[letter.toUpperCase()] ?? [])].sort();
}

export function replaceAt(word: string, position: number, letter: string): string {
  return word.slice(0, position) + letter + word.slice(position + 1);
}

/** Sorted-letters key used to bucket anagrams. */
export function anagramKey(word: string): string {
  return [...word.toUpperCase()].sort().join('');
}

/** Valid dictionary anagrams of `word`, excluding the word itself. */
export function findAnagrams(word: string, dictionary: Set<string>): string[] {
  const key = anagramKey(word);
  const out: string[] = [];
  for (const candidate of dictionary) {
    if (candidate.length !== word.length) continue;
    if (candidate === word) continue;
    if (anagramKey(candidate) === key) out.push(candidate);
  }
  return out.sort();
}

export type MoveResult =
  | { kind: 'success'; move: PivotMove }
  | { kind: 'invalidWord'; word: string }
  | { kind: 'invalidOperation'; reason: string }
  | { kind: 'sameAsCurrent' };

export function applySwap(
  currentWord: string,
  position: number,
  newLetter: string,
  isValidWord: (w: string) => boolean,
): MoveResult {
  if (position < 0 || position >= currentWord.length) {
    return { kind: 'invalidOperation', reason: 'Position out of range' };
  }
  const letter = newLetter.toUpperCase();
  if (currentWord[position] === letter) return { kind: 'sameAsCurrent' };

  const candidate = replaceAt(currentWord, position, letter);
  if (!isValidWord(candidate)) return { kind: 'invalidWord', word: candidate };

  return {
    kind: 'success',
    move: { operation: 'swap', fromWord: currentWord, toWord: candidate, position },
  };
}

export function applyBend(
  currentWord: string,
  position: number,
  newLetter: string,
  isValidWord: (w: string) => boolean,
): MoveResult {
  if (position < 0 || position >= currentWord.length) {
    return { kind: 'invalidOperation', reason: 'Position out of range' };
  }
  const letter = newLetter.toUpperCase();
  const original = currentWord[position];
  if (original === letter) return { kind: 'sameAsCurrent' };
  if (!areAdjacent(original, letter)) {
    return {
      kind: 'invalidOperation',
      reason: `'${letter}' is not keyboard-adjacent to '${original}'`,
    };
  }

  const candidate = replaceAt(currentWord, position, letter);
  if (!isValidWord(candidate)) return { kind: 'invalidWord', word: candidate };

  return {
    kind: 'success',
    move: { operation: 'bend', fromWord: currentWord, toWord: candidate, position },
  };
}

export function applySlide(
  currentWord: string,
  anagram: string,
  isValidWord: (w: string) => boolean,
): MoveResult {
  const target = anagram.toUpperCase();
  if (target.length !== currentWord.length) {
    return { kind: 'invalidOperation', reason: 'Slide must preserve length' };
  }
  if (anagramKey(target) !== anagramKey(currentWord)) {
    return { kind: 'invalidOperation', reason: 'Not an anagram of the current word' };
  }
  if (target === currentWord) return { kind: 'sameAsCurrent' };
  if (!isValidWord(target)) return { kind: 'invalidWord', word: target };

  return {
    kind: 'success',
    move: { operation: 'slide', fromWord: currentWord, toWord: target, position: null },
  };
}

/** Bends are excluded — they never count against par or the life budget. */
export function billableMoveCount(moves: PivotMove[]): number {
  return moves.filter((m) => m.operation !== 'bend').length;
}

export function bendCount(moves: PivotMove[]): number {
  return moves.filter((m) => m.operation === 'bend').length;
}

export function freeMoveBudget(puzzle: PivotPuzzle): number {
  return puzzle.par + MAX_FREE_MOVES_PAST_PAR;
}

/**
 * True iff the committed moves exactly match the leading moves of the par
 * solution — only then can the hint hand back the next optimal step.
 */
export function isOnParPath(puzzle: PivotPuzzle, moves: PivotMove[]): boolean {
  if (moves.length > puzzle.parSolution.length) return false;
  return moves.every(
    (m, i) =>
      m.toWord === puzzle.parSolution[i].toWord &&
      m.operation === puzzle.parSolution[i].operation,
  );
}

export function nextParMove(puzzle: PivotPuzzle, moves: PivotMove[]): PivotMove | null {
  if (!isOnParPath(puzzle, moves)) return null;
  if (moves.length >= puzzle.parSolution.length) return null;
  return puzzle.parSolution[moves.length];
}

/** First position of `currentWord` that doesn't match the target. */
export function firstMismatchPosition(
  currentWord: string,
  targetWord: string,
): number | null {
  if (currentWord.length !== targetWord.length) return null;
  for (let i = 0; i < currentWord.length; i++) {
    if (currentWord[i] !== targetWord[i]) return i;
  }
  return null;
}

export interface HintHighlight {
  position: number;
  targetLetter: string;
}

/**
 * Hints only ever highlight — the player still commits the move. Prefer the
 * position the next par move targets; otherwise the first letter that doesn't
 * match the target.
 */
export function computeHint(
  puzzle: PivotPuzzle,
  currentWord: string,
  moves: PivotMove[],
): HintHighlight | null {
  const next = nextParMove(puzzle, moves);
  if (next && next.position != null) {
    const letter = next.toWord[next.position];
    if (letter) return { position: next.position, targetLetter: letter };
  }
  const pos = firstMismatchPosition(currentWord, puzzle.targetWord);
  if (pos != null) return { position: pos, targetLetter: puzzle.targetWord[pos] };
  return null;
}
